import type { Category } from "mws-ts-v2-sdk";

import {
  eventsOverviewPageSizes,
  usePrefetchEventsOverviewInfiniteQuery,
} from "~/queries/catalog/events";
import { CategoryTabs } from "~/types/Category";

export const useCategoryTabs = (
  category: MaybeRefOrGetter<Category>,
  showHistoricalEvents = () => true,
) => {
  const { t } = useI18n();
  const route = useRoute();

  const tabId = useRouteQuery<CategoryTabs | undefined>(
    "tab",
    () => transformRouteParams(route.query.tab, "string"),
    {
      transform: (tab) => {
        // Only allow known values
        if (
          tab === CategoryTabs.Historical ||
          tab === CategoryTabs.Live ||
          tab === CategoryTabs.Upcoming
        ) {
          return tab;
        }
      },
    },
  );

  const tabs = computed(() => {
    const tabsToShow = [
      {
        title: t("category.tabs.active"),
        id: CategoryTabs.Live,
      },
    ];

    if (toValue(category).showUpcomingEvents) {
      tabsToShow.splice(1, 0, {
        title: t("category.tabs.upcoming"),
        id: CategoryTabs.Upcoming,
      });
    }

    if (toValue(showHistoricalEvents)) {
      tabsToShow.push({
        title: t("category.tabs.history"),
        id: CategoryTabs.Historical,
      });
    }

    return tabsToShow;
  });

  const prefetchEventsOverview = usePrefetchEventsOverviewInfiniteQuery();
  watchEffect(async () => {
    if (tabId.value) return;

    const categoryValue = toValue(category);

    // Prefetch the live events and see if we have them. If so, navigate to the live tab.
    const liveEvents = await prefetchEventsOverview({
      liveStatus: CategoryTabs.Live,
      categoryId: categoryValue.id,
      pageSize: eventsOverviewPageSizes.category,
    });

    if (liveEvents.pages?.[0] && liveEvents.pages[0].totalCount > 0) {
      return (tabId.value = CategoryTabs.Live);
    }

    // Prefetch the upcoming events and see if we have them. If so, navigate to the upcoming tab.
    // Only do this when the category allows for upcoming events.
    if (categoryValue.showUpcomingEvents) {
      const upcomingEvents = await prefetchEventsOverview({
        liveStatus: CategoryTabs.Upcoming,
        categoryId: categoryValue.id,
        pageSize: eventsOverviewPageSizes.category,
      });

      if (upcomingEvents.pages?.[0] && upcomingEvents.pages[0].totalCount > 0) {
        return (tabId.value = CategoryTabs.Upcoming);
      }
    }

    // If the other tabs don't return anything, prefetch the historical events and navigate
    await prefetchEventsOverview({
      liveStatus: CategoryTabs.Historical,
      categoryId: categoryValue.id,
      pageSize: eventsOverviewPageSizes.category,
    });

    return (tabId.value = CategoryTabs.Historical);
  });

  return {
    tabId,
    tabs,
  };
};

export default useCategoryTabs;
